/**
 * Email Verification Screen
 * Shows after signup - user enters 6-digit code from email
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MFACodeInput } from './MFACodeInput';
import { Button } from '@/components/ui/button';
import { Mail, RefreshCw } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

interface EmailVerificationScreenProps {
  email: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function EmailVerificationScreen({
  email,
  onVerified,
  onCancel
}: EmailVerificationScreenProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const handleVerifyCode = async (code: string) => {
    setIsVerifying(true);
    setError(null);

    try {
      logger.info('📧 Verifying email code:', { email, codeLength: code.length });

      // Verify the 6-digit OTP code for signup
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email,
        token: code,
        type: 'signup' // Use 'signup' type for email verification during signup
      });

      if (verifyError) {
        logger.error('❌ Email verification failed:', verifyError);
        throw verifyError;
      }

      if (data.user) {
        logger.info('✅ Email verified successfully!', { userId: data.user.id });

        toast({
          title: 'Email Verified!',
          description: 'Your account has been verified successfully.',
        });

        // Call success callback
        onVerified();
      } else {
        throw new Error('Verification succeeded but no user returned');
      }
    } catch (error: any) {
      logger.error('❌ Email verification error:', error);

      let errorMessage = 'Invalid or expired code. Please try again.';

      if (error.message?.includes('expired')) {
        errorMessage = 'This code has expired. Please request a new one.';
      } else if (error.message?.includes('invalid')) {
        errorMessage = 'Invalid code. Please check and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);

      toast({
        title: 'Verification Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError(null);

    try {
      logger.info('📧 Resending verification code to:', email);

      // Resend signup confirmation OTP
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (resendError) throw resendError;

      toast({
        title: 'Code Resent!',
        description: 'A new verification code has been sent to your email.',
      });

      logger.info('✅ Verification code resent successfully');
    } catch (error: any) {
      logger.error('❌ Failed to resend code:', error);

      toast({
        title: 'Failed to Resend',
        description: error.message || 'Could not resend code. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-6 py-4">
      {/* Success message */}
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">Check Your Email</h3>
        <p className="text-sm text-muted-foreground">
          We've sent a 6-digit verification code to:
        </p>
        <p className="text-sm font-medium">{email}</p>
      </div>

      {/* Code input */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-center block">
          Enter Verification Code
        </label>
        <MFACodeInput
          onComplete={handleVerifyCode}
          isVerifying={isVerifying}
          error={error}
          autoFocus={true}
        />
      </div>

      {/* Resend button */}
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Didn't receive the code?
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleResendCode}
          disabled={resending || isVerifying}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
          Resend Code
        </Button>
      </div>

      {/* Cancel button */}
      <div className="pt-4 border-t">
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={isVerifying}
          className="w-full"
        >
          Back to Signup
        </Button>
      </div>
    </div>
  );
}
